// ─── Auth & User ───────────────────────────────────────────────────────────────

/** Tên role thực tế từ API (roleName field) */
export type ApiRoleName =
  | 'Admin'
  | 'RestaurantSeller' // Người Bán
  | 'RestaurantBuyer' // Người Mua
  | 'Landlord' // Chủ nhà
  | 'PropertySeeker' // Người tìm thuê
  | 'Headhunter' // Nhà tuyển dụng
  | 'JobSeeker' // Người tìm việc
  | 'FurnitureSeller' // Bán nội thất
  | 'FurnitureBuyer' // Mua nội thất
  | 'Franchiser' // Chủ thương hiệu
  | 'FranchiseSeeker' // Tìm nhượng quyền
  | string;

export type UserRole =
  | 'admin'
  | 'restaurantseller'
  | 'restaurantbuyer'
  | 'landlord'
  | 'propertyseeker'
  | 'headhunter'
  | 'jobseeker'
  | 'furnitureseller'
  | 'furniturebuyer'
  | 'franchiser'
  | 'franchiseseeker'
  | string;

/** Một role entry trong mảng roles[] từ API */
export interface ApiRole {
  id: string;
  roleName: ApiRoleName;
  description: string;
  createdAt: string;
  updatedAt: string;
  UserRole: {
    id: string;
    userId: string;
    roleId: string;
    createdAt: string;
    updatedAt: string;
  };
}

/** Shape của /users/profile trả về (bọc trong { data: ... }) */
export interface ApiUserProfile {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  title?: string;
  note?: string;
  createdBy?: string;
  updatedBy?: string;
  approvedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string | null;
  approvalStatus: 'approved' | 'pending' | 'rejected';
  isActive: boolean;
  roles: ApiRole[];
}

/** Shape chuẩn hoá dùng trong toàn bộ app */
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  /** firstName + lastName ghép lại */
  fullName: string;
  company?: string;
  title?: string;
  /** Role primary (đầu tiên trong mảng, lowercase) */
  role: UserRole;
  /** Tất cả roles thô từ API */
  roles: ApiRole[];
  isActive: boolean;
  approvalStatus: string;
  createdAt: string;
  updatedAt: string;
}

/** Hàm helper: chuyển ApiUserProfile → User */
export function normalizeUser(profile: ApiUserProfile): User {
  const primaryRoleName = profile.roles?.[0]?.roleName ?? 'JobSeeker';
  return {
    id: profile.id,
    username: profile.username,
    email: profile.email,
    firstName: profile.firstName,
    lastName: profile.lastName,
    fullName: `${profile.firstName} ${profile.lastName}`.trim(),
    company: profile.company,
    title: profile.title,
    role: primaryRoleName.toLowerCase() as UserRole,
    roles: profile.roles,
    isActive: profile.isActive,
    approvalStatus: profile.approvalStatus,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
  };
}

export interface LoginRequest {
  username?: string;
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  note?: string;
}

/** Login chỉ trả access_token + refresh_token */
export interface LoginApiResponse {
  access_token: string;
  refresh_token: string;
}

/** Profile API trả { data: ApiUserProfile } */
export interface ProfileApiResponse {
  data: ApiUserProfile;
}
